import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import Text, { type TextVariant } from '../base/Text';

export default function Switch({
  left,
  right,
  variant = 'studio/body/medium/md',
  className = '',
  defaultValue = false,
  superscript,
  onChange,
}: {
  left: string;
  right: string;
  variant?: TextVariant;
  className?: string;
  defaultValue?: boolean;
  superscript?: null | string;
  onChange?: (checked: boolean) => void;
}) {
  const [active, setActive] = useState(defaultValue);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const [highlight, setHighlight] = useState({ left: 0, width: 0 });
  const hasMeasured = useRef(false);

  const updateHighlight = useCallback(() => {
    const el = active ? rightRef.current : leftRef.current;
    if (el) {
      setHighlight({
        left: el.offsetLeft,
        width: el.offsetWidth,
      });
      hasMeasured.current = true;
    }
  }, [active]);

  // Measure before paint to avoid flash of incorrect position
  useLayoutEffect(() => {
    updateHighlight();
  }, [updateHighlight]);

  useEffect(() => {
    if (onChange) {
      onChange(active);
    }
  }, [onChange, active]);

  return (
    <button
      className={`bg-gray-30 border-gray-30 dark:bg-gray-75 dark:border-gray-75 relative mr-4 inline-flex flex-row flex-nowrap items-center rounded-[12px] border-4 ${className}`}
      onClick={() => setActive((a) => !a)}
    >
      <div
        className={`absolute top-0 z-0 h-full rounded-lg bg-white ${hasMeasured.current ? 'transition-all duration-200' : ''}`}
        style={{
          left: highlight.left,
          width: highlight.width,
        }}
      />
      <div ref={leftRef} className="z-10 px-3 py-1">
        <Text
          as="span"
          className={`whitespace-nowrap transition-colors ${active ? 'text-gray-60' : 'text-gray-90'}`}
          variant={variant}
        >
          {left}
        </Text>
      </div>
      <div ref={rightRef} className="z-10 px-3 py-1">
        <Text
          as="span"
          className={`whitespace-nowrap transition-colors ${active ? 'text-gray-90' : 'text-gray-60'}`}
          variant={variant}
        >
          {right}
        </Text>
      </div>
      {superscript && (
        <Text
          as="div"
          variant="studio/body/xs"
          className={`${active ? 'bg-blue' : 'bg-gray-45'} pointer-events-none absolute right-0 top-0 z-10 -mr-4 -mt-4 rounded-full px-3 py-1 text-white transition-colors`}
        >
          {superscript}
        </Text>
      )}
    </button>
  );
}
